import "./Login.css";
import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { auth, GoogleAuthProvider, FacebookAuthProvider } from "../Firebase";
import { isFacebookLoginOn, facebookErrorMessage, facebookLinkEmail, linkHeldFacebook } from "../utils/facebookLogin";
import FacebookLinkNotice from '../components/FacebookLinkNotice';
import { signInWith, takeRedirectSignIn, hasPendingRedirect } from "../utils/socialSignIn";
import SigningInOverlay from "../components/SigningInOverlay";
import { useLogin } from "../hooks/useLogin";
import { useAuthContext } from "../hooks/useAuthContext";
import { useInAppGoogleGuard } from "../components/OpenInBrowserPrompt";
// Email verification now handled only for connections, not login

const LoginForm = () => {
  const { guard, prompt: openInBrowserPrompt } = useInAppGoogleGuard(undefined, 'Log in with email instead');
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  // Facebook found an existing account with this email (null = no).
  const [fbLinkEmail, setFbLinkEmail] = useState(null);
  // Back from Google / Facebook (redirect), or the pop-up just finished:
  // "Signing you in…" until we move on, not a flash of this form.
  const [finishing, setFinishing] = useState(() => hasPendingRedirect('login'));
  const [verificationMessage, setVerificationMessage] = useState("");
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useLogin();
  const { user } = useAuthContext();
  // Email verification loading state (minimal usage for redirect timing)
  const emailVerificationLoading = false;

  // Check for email verification confirmation
  useEffect(() => {
    const verified = searchParams.get('verified');
    const continueUrl = searchParams.get('continueUrl');
    if (verified === 'true') {
      const destination = continueUrl ? 'onboarding' : 'profile setup';
      setVerificationMessage(`✅ Your email has been verified! Please log in to continue to your ${destination}.`);
      console.log('[Login] Email verification confirmed via URL parameter');
    }
  }, [searchParams]);

  // Check if user is already logged in - redirect directly (no email verification check needed)
  useEffect(() => {
    if (user && !emailVerificationLoading) {
      // User is authenticated, redirect them (email verification handled separately for connections)
      console.log('[Login] User already logged in, redirecting...');
      navigate('/ProfileUpdate');
    }
  }, [user, emailVerificationLoading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoginError(""); // Clear previous errors
    
    try {
      console.log('[Login] Attempting to sign in with Firebase...');
      const userCredential = await auth.signInWithEmailAndPassword(email, password);
      const user = userCredential.user;
      console.log('[Login] Firebase sign-in successful, user:', user.uid);
      console.log('[Login] User email verified:', user.emailVerified);
      
      // Email verification is handled separately for connections, not required for login
      console.log(`[Login] User logged in successfully, email verified: ${user.emailVerified}`);
      
      console.log('[Login] Calling backend login...');
      const result = await login(user.uid);
      console.log('[Login] Backend login result:', result);
      
      if (result) {
        console.log('[Login] Login successful');
        
        // Check if this login is after email verification
        const verified = searchParams.get('verified');
        const continueUrl = searchParams.get('continueUrl');
        
        const backUrl = searchParams.get('back');
        if (verified === 'true') {
          const redirectUrl = continueUrl ? decodeURIComponent(continueUrl) : '/Onboarding';
          console.log('[Login] User just verified email, redirecting to:', redirectUrl);
          navigate(redirectUrl);
        } else if (backUrl) {
          navigate(backUrl);
        } else {
          console.log('[Login] Regular login, redirecting to ProfileUpdate');
          navigate('/ProfileUpdate');
        }
      } else {
        console.log('[Login] Backend login failed');
        setLoginError('Login failed. Please try again.');
      }
    } catch (error) {
      console.error('[Login] Error:', error);
      if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
        setLoginError('Invalid email or password.');
      } else if (error.code === 'auth/invalid-credential') {
        setLoginError('Invalid email or password.');
      } else {
        setLoginError(`An error occurred during login: ${error.message}`);
      }
    }
  };

  // After Google / Facebook sign-in (pop-up, or back from a redirect).
  const finishSocialSignIn = async (result, provider) => {
    setFinishing(true);
    try {
      if (provider === 'google') await linkHeldFacebook(result.user); // after "already have an account" on Facebook
      // Only go on once their account has loaded — otherwise the profile
      // page would sit on "Loading your profile..." with no explanation.
      const ok = await login(result.user.uid);
      if (!ok) {
        setFinishing(false);
        setLoginError("You're signed in with Google, but we couldn't load your account. Please try again — and if it keeps happening, email us and we'll sort it out.");
        return;
      }
      const backUrl = searchParams.get('back');
      navigate(backUrl || '/ProfileUpdate');
    } catch (error) {
      setFinishing(false);
      throw error;
    }
  };

  const googleError = (error) => {
    console.error('[Login] Google sign-in error:', error.code, error.message);
    if (error.code === 'auth/unauthorized-domain') {
      setLoginError('Sign-in is not configured for this domain. Please contact support.');
    } else if (error.code === 'auth/popup-closed-by-user') {
      setLoginError('Sign-in was cancelled.');
    } else {
      setLoginError('Google sign-in failed. Please try again.');
    }
  };

  const facebookError = (error) => {
    console.error('[Login] Facebook sign-in error:', error.code, error.message);
    const linkEmail = facebookLinkEmail(error);
    if (linkEmail !== null) { setFbLinkEmail(linkEmail); return; }
    setLoginError(facebookErrorMessage(error) || 'Sign-in was cancelled.');
  };

  const handleGoogleSignIn = async (e) => {
    e?.preventDefault?.();
    try {
      const result = await signInWith(new GoogleAuthProvider(), { page: 'login' });
      if (result) await finishSocialSignIn(result, 'google');
    } catch (error) {
      googleError(error);
    }
  };

  const handleFacebookSignIn = async (e) => {
    e?.preventDefault?.();
    setLoginError('');
    try {
      const provider = new FacebookAuthProvider();
      provider.addScope('email');
      const result = await signInWith(provider, { page: 'login' });
      if (result) await finishSocialSignIn(result, 'facebook');
    } catch (error) {
      facebookError(error);
    }
  };

  // Back from a Google / Facebook redirect (phones, in-app browsers).
  useEffect(() => {
    let cancelled = false;
    takeRedirectSignIn('login').then(async (back) => {
      if (cancelled) return;
      if (!back) { setFinishing(false); return; }
      if (back.error) {
        setFinishing(false);
        if (back.pending.provider === 'facebook') facebookError(back.error); else googleError(back.error);
        return;
      }
      try {
        await finishSocialSignIn(back.result, back.pending.provider);
      } catch (error) {
        if (back.pending.provider === 'facebook') facebookError(error); else googleError(error);
      }
    });
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEmailLinkSignIn = async (e) => {
    e.preventDefault();
    if (!email) {
      setLoginError('Enter your email address above first.');
      return;
    }
    try {
      const actionCodeSettings = {
        url: `${window.location.origin}/ProfileUpdate`,
        handleCodeInApp: true,
      };
      await auth.sendSignInLinkToEmail(email, actionCodeSettings);
      window.localStorage.setItem('emailForSignIn', email);
      setVerificationMessage(`✅ Sign-in link sent to ${email} — check your inbox.`);
      setLoginError('');
    } catch (error) {
      setLoginError('Could not send sign-in link. Please try again.');
    }
  };

  if (finishing) return <SigningInOverlay />;

  return (
    <div className="login-page-bg">
      <form className="form-container login-form" onSubmit={handleSubmit}>
        <h1 className="form-heading">Login</h1>

        {verificationMessage && (
          <div className="form-success">
            {verificationMessage}
          </div>
        )}
        {loginError && <div className="form-error">{loginError}</div>}

        {/* Google / Facebook first, as on the sign-up form — on phones the
            email fields would push them off screen. */}
        <div className="social-buttons">
          <button
            className="social-btn social-btn-google"
            type="button"
            onClick={guard(handleGoogleSignIn)}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" style={{flexShrink:0}}>
              <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908C16.618 14.013 17.64 11.706 17.64 9.2z"/>
              <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
              <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
              <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z"/>
            </svg>
            Continue with Google
          </button>
          {openInBrowserPrompt}
          {/* Hidden until the Meta app is Live — see utils/facebookLogin.js. */}
          {isFacebookLoginOn() && (
          <button
            className="social-btn social-btn-facebook"
            type="button"
            onClick={handleFacebookSignIn}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" style={{flexShrink:0}}>
              <path fill="#fff" d="M18 9a9 9 0 1 0-10.406 8.89v-6.29H5.309V9h2.285V7.017c0-2.256 1.344-3.503 3.4-3.503.985 0 2.015.176 2.015.176v2.215h-1.135c-1.118 0-1.467.694-1.467 1.406V9h2.496l-.4 2.6H10.41v6.29A9.002 9.002 0 0 0 18 9z"/>
            </svg>
            Continue with Facebook
          </button>
          )}
          {fbLinkEmail !== null && <FacebookLinkNotice email={fbLinkEmail} onGoogle={guard(handleGoogleSignIn)} onClose={() => setFbLinkEmail(null)} />}
        </div>

        <div className="login-divider"><span>or log in with email</span></div>

        <label className="form-label" htmlFor="login-email">Email</label>
        <input
          className="form-input"
          id="login-email"
          type="email"
          autoComplete="username"
          onChange={(e) => setEmail(e.target.value)}
          value={email}
          required
        />

        <label className="form-label" htmlFor="login-password">Password</label>
        <input
          className="form-input"
          id="login-password"
          type="password"
          autoComplete="current-password"
          onChange={(e) => setPassword(e.target.value)}
          value={password}
          required
        />

        <button className="button w-full" type="submit">Login</button>
        <div className="social-buttons login-email-link">
          <button
            className="social-btn social-btn-email-link"
            type="button"
            onClick={handleEmailLinkSignIn}
          >
            ✉️ Email me a sign-in link
          </button>
        </div>
        <div className="login-links">
          <Link to="/forgot-password" className="forgot-link">Forgot Password?</Link>
          <span className="divider">|</span>
          <Link to="/register" className="register-link">Register</Link>
        </div>
      </form>
    </div>
  );
};

export default LoginForm;

// import { useEffect} from "react"
// import { useCommunitiesContext } from "../hooks/useCommunitiesContext"
// import "./Login.css";
// import LoginForm from "../components/LoginForm"

// const Login = () => {
  

//   return (
    
  
//     <div className="ijoin">

//       <img
//         src="https://t3.ftcdn.net/jpg/02/48/42/64/360_F_248426448_NVKLywWqArG2ADUxDq6QprtIzsF82dMF.jpg" // Replace with the actual image path or URL
//         alt="Image place holder"
//         className="community-image" // Add a class for styling if needed
//       />
      

//       <div className="i-community">
      
//         <LoginForm/>
     
//       </div>
//       </div>

//   )
// }

// export default Login